// src/features/editor/components/studio/EditorHeader.tsx
import { useNavigate } from 'react-router-dom'
import { Home, Settings } from 'lucide-react'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
  TooltipProvider,
} from '@teach/ui'
import { HEADER_RIGHT_MIN_W_CLASS } from './layout'
import { useIsMobile } from '@/hooks/use-mobile'
import { useSettingsPageOnPhone } from './useSettingsPageOnPhone'
import { cn } from '@/lib/utils'
import type { CourseWithoutBlocks } from '@/types/course'
import { useAppDispatch } from '@/store/hooks'
import { setDialogOpen } from '@/features/editor/editorSlice'
import { useIsCourseAuthor } from '@/features/editor/hooks/useIsCourseAuthor'
import { TitleInput } from '@/features/editor/components/EditorHeader/TitleInput'
import { TooltipIconButton } from '@/features/editor/components/EditorHeader/TooltipIconButton'
import { HeaderToolsGroup } from '@/features/editor/components/EditorHeader/HeaderToolsGroup'
import { ContentLangSwitcher } from '@/features/editor/components/EditorHeader/ContentLangSwitcher'
import { ReportBugHeaderButton } from '@/features/support/ReportBugButton'
import { HeaderRightCluster } from '@/features/editor/components/EditorHeader/HeaderRightCluster'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  course: CourseWithoutBlocks
  /** Whether the AI sidebar is expanded — when collapsed the left group clears the diamond logo. */
  aiOpen: boolean
}

export const EditorHeader = ({ course, aiOpen }: Props) => {
  const m = useMessages(messages)
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const isCourseAuthor = useIsCourseAuthor(course)

  const isMobile = useIsMobile()
  useSettingsPageOnPhone(course.id)
  const openSettings = () => dispatch(setDialogOpen({ dialog: 'settingsOpen', open: true }))
  const openDrawImages = () => dispatch(setDialogOpen({ dialog: 'drawImagesOpen', open: true }))

  return (
    <TooltipProvider delayDuration={200}>
      <header
        data-testid="editor-header"
        className="flex h-14 short:h-11 items-center gap-1 overflow-hidden bg-surface px-2 sm:gap-2 sm:px-4"
      >
        {/* Left — breadcrumb (Home → title) + course tools; ml-11 clears the diamond when collapsed */}
        <div
          className={cn(
            'flex min-w-0 flex-1 items-center gap-2 overflow-hidden',
            !aiOpen && 'ml-11',
          )}
        >
          <Breadcrumb className="min-w-0 flex-1">
            <BreadcrumbList className="min-w-0 flex-nowrap">
              <BreadcrumbItem className="shrink-0">
                <BreadcrumbLink asChild>
                  <button
                    // Домой, а не шаг назад: предыдущей в истории могла быть эта же страница редактора
                    // (перезагрузка, переход по ссылке), и кнопка никуда не вела.
                    onClick={() => navigate('/dashboard')}
                    aria-label={m.a11yBack}
                    className="flex items-center rounded-lg p-2 text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground"
                  >
                    <Home className="h-4 w-4" />
                  </button>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator className="shrink-0" />
              <BreadcrumbItem className="min-w-0">
                <TitleInput course={course} />
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <HeaderToolsGroup
            course={course}
            isCourseAuthor={isCourseAuthor}
            onOpenDrawImages={openDrawImages}
          />
        </div>

        {/* Right — save status · preview · publish · настройки курса (правее публикации) */}
        <div
          className={cn(
            'flex shrink-0 items-center justify-end gap-1 sm:gap-2',
            HEADER_RIGHT_MIN_W_CLASS,
          )}
        >
          {isMobile ? <ContentLangSwitcher course={course} sheet /> : null}
          {/* «Нашли баг?» — на телефоне только жучок, с `lg` с подписью. */}
          <ReportBugHeaderButton />
          <HeaderRightCluster courseId={course.id} isCourseAuthor={isCourseAuthor} />
          <TooltipIconButton
            icon={Settings}
            tooltip={m.courseSettings}
            onClick={openSettings}
            ariaLabel={m.courseSettings}
          />
        </div>
      </header>
    </TooltipProvider>
  )
}
